/**
 * A project checklist is one row: the project and its tasks live in the same
 * JSON column. A client-generated id makes a retry update that row instead of
 * inserting a second project.
 */

const { isConnectionFailure } = require('./connectionCopy');
const { RETRY_BACKOFF_MS } = require('./timedFetch');

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUuid(value) {
  return UUID_RE.test(String(value || ''));
}

function newId(idFactory) {
  if (typeof idFactory === 'function') return idFactory();
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  throw new Error('crypto.randomUUID is not available');
}

function creatingFrom(source) {
  if (!source) return true;
  if (source.editing) return false;
  if (source.creating === false) return false;
  return true;
}

function taskForSave(task, creating, idFactory) {
  const source = Object.assign({}, task || {});
  delete source.photos;
  if (isUuid(source.id)) {
    source.id = String(source.id);
    return source;
  }
  if (!creating && source.id != null && source.id !== '') return source;
  source.id = newId(idFactory);
  return source;
}

function checklistWrite(input) {
  const source = input || {};
  const creating = creatingFrom(source);
  const idFactory = source.idFactory;
  const id = isUuid(source.id) ? String(source.id) : newId(idFactory);
  const tasks = (Array.isArray(source.tasks) ? source.tasks : []).map((task) => (
    taskForSave(task, creating, idFactory)
  ));
  const row = {
    id,
    title: source.title,
    description: source.description || '',
    client_id: source.clientId,
    due_date: source.dueDate || null,
    tasks,
    updated_at: source.updatedAt || new Date().toISOString(),
  };
  if (creating) {
    row.agent_id = source.agentId;
    row.status = 'active';
    row.created_by_role = 'agent';
  }
  return row;
}

async function runWithNetworkRetry(run, options) {
  const settings = options || {};
  const attempts = settings.attempts || 2;
  const sleep = settings.sleep || ((ms) => new Promise((resolve) => setTimeout(resolve, ms)));
  const backoffMs = settings.backoffMs != null ? settings.backoffMs : RETRY_BACKOFF_MS;
  let lastError = null;
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      return await run();
    } catch (error) {
      lastError = error;
      if (!isConnectionFailure(error) || attempt === attempts) throw error;
      if (settings.onRetry) settings.onRetry();
      if (backoffMs > 0) await sleep(backoffMs);
    }
  }
  throw lastError;
}

async function saveProjectChecklist(supabase, input, options) {
  const creating = creatingFrom(input);
  const row = input && input.row ? input.row : checklistWrite(input);
  const data = await runWithNetworkRetry(async () => {
    const query = creating
      ? supabase.from('projects').upsert(row, { onConflict: 'id' }).select('*, clients(email, first_name)').single()
      : supabase.from('projects').update({
        title: row.title,
        description: row.description,
        client_id: row.client_id,
        due_date: row.due_date,
        tasks: row.tasks,
        updated_at: row.updated_at,
      }).eq('id', row.id);
    const result = await query;
    if (result && result.error) {
      const failure = new Error(result.error.message || 'Error saving project');
      failure.code = result.error.code;
      failure.details = result.error;
      throw failure;
    }
    return result ? result.data : null;
  }, options);
  return { row, data };
}

module.exports = {
  isUuid,
  checklistWrite,
  runWithNetworkRetry,
  saveProjectChecklist,
};
